import { useEffect, useRef, useState } from "react";
import type { Dispatch, ReactElement, ReactNode, SetStateAction } from "react";
import type { ApiClient, FieldSuggestion, KundeAkte, KundeStamm, LogoCandidate } from "@kundeninfo/api-client";
import { Arbeitsdialog, Feld, useBeforeUnload, useBestaetigung } from "@kundeninfo/ui";
import type { useFormularZustand } from "@kundeninfo/ui";
import { Auswahl, Eingabe, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import type { VerlassenWaechter } from "../../router.js";
import { KiKnopf } from "../../components/meHuellen.js";
import { Ortszeile } from "../../components/Ortszeile.js";
import { useLaender } from "../../laender.js";
import { LOGO_TEXT, PARTNER_FORM_TEXT as T, SOURCE_LABEL } from "./texts.js";
import { LogoChoice, WebsiteLogoLookup, showsLogoChoice, websiteForLookup } from "./LogoChoice.js";
import "./GeschaeftspartnerDialog.css";

/*
 * DER EINE DIALOG für „Geschäftspartner anlegen“ und „Stammdaten bearbeiten“ (Entscheide 814–819, Dirk 05.11.2026).
 * Felder, Reihenfolge, Raster, Vorschläge und Logo-Weg stehen nur hier; was fachlich nur einen Modus betrifft, reicht der
 * Aufrufer als Slot herein: beim Anlegen die Dublettentreffer (`nachFirmenname`) und den Beziehungsblock (`beziehung`),
 * beim Bearbeiten die Sperrleiste (`meldungen`) und den Konflikthinweis (`hinweis`). Den Schreibweg, die Sperre und die
 * Wege hinaus behält der Aufrufer — der Dialog zeichnet und schlägt vor, er speichert nie.
 */

/** Die Formularwerte — überall Zeichenketten; leer wird erst beim Absenden zu `null`. `country` ist nie leer (NOT-NULL-
 *  Spalte, Vorgabe DE), `invoiceFormat` einer der drei Vertragsschlüssel. */
export type PartnerDraft = {
  companyName: string; street: string; houseNumber: string; postalCode: string; city: string;
  phone: string; email: string; website: string; vatId: string; industry: string;
  country: string; leitwegId: string; invoiceFormat: InvoiceFormat;
};

export type InvoiceFormat = NonNullable<KundeStamm["invoiceFormat"]>;
type Field = keyof PartnerDraft;

/** ALLE Felder in der Folge der Leseansicht — für Gleichheit, Feldfehler (422) samt Beschriftungen und den Vergleich im
 *  fremden Stand. Eine zweite, kürzere Liste an einer dieser Stellen ließe Land oder Leitweg-ID dort wieder herausfallen. */
export const PARTNER_FIELDS: readonly { name: Field; label: string }[] = (
  ["companyName", "street", "houseNumber", "postalCode", "city", "phone", "email", "website", "vatId", "industry",
    "country", "leitwegId", "invoiceFormat"] as const
).map((name) => ({ name, label: T.field[name] }));

/** Die Beschriftungen aller Stammdatenfelder als Karte — für die Sammelmeldung von `useFormularZustand`. */
export const PARTNER_LABELS: Readonly<Record<string, string>> =
  Object.fromEntries(PARTNER_FIELDS.map((f) => [f.name, f.label]));

/** Die Kennung eines Feldes im Dialog (auch der Felder, die ein Aufrufer über die Slots beisteuert) — ein Präfix für
 *  beide Modi. */
const ID_PREFIX = "gp";
export const partnerFieldId = (name: string): string => `${ID_PREFIX}-${name}`;

export const INVOICE_FORMATS = Object.keys(T.invoiceFormats) as InvoiceFormat[];

/** Die Beschriftung eines Rechnungsformats; ein unbekannter Schlüssel bleibt, wie er ist. */
export const invoiceFormatText = (wert: string): string => T.invoiceFormats[wert as InvoiceFormat] ?? wert;

/** Die leere Anlege-Maske — Rechnungsformat ZUGFeRD (816); die Server-/DB-Vorgabe PDF_A3 für andere Aufrufer bleibt. */
export const EMPTY_DRAFT: PartnerDraft = {
  companyName: "", street: "", houseNumber: "", postalCode: "", city: "", phone: "", email: "", website: "", vatId: "",
  industry: "", country: "DE", leitwegId: "", invoiceFormat: "ZUGFERD_EN16931",
};

/** Der Entwurf aus der Akte. `streetName` und NICHT `street`: `street` ist die fertige Zeile („Hafenstraße 12a“) und
 *  käme beim Speichern mit der Hausnummer doppelt heraus. */
export function draftFrom(akte: KundeAkte): PartnerDraft {
  return {
    companyName: akte.companyName, street: akte.streetName ?? "", houseNumber: akte.houseNumber ?? "",
    postalCode: akte.postalCode ?? "", city: akte.city ?? "", phone: akte.phone ?? "", email: akte.email ?? "",
    website: akte.website ?? "", vatId: akte.vatId ?? "", industry: akte.industry ?? "", country: akte.country,
    leitwegId: akte.leitwegId ?? "", invoiceFormat: akte.invoiceFormat ?? "PDF_A3",
  };
}

/** Leer wird `null`, sonst der getrimmte Wert — für die Stammdaten wie für die Beziehungswerte des Anlegens. */
export const orNull = (wert: string): string | null => (wert.trim() === "" ? null : wert.trim());

/**
 * IMMER alle Felder: Der Schreibweg der Stammdaten ist Vollersatz — eine Maske, die Land, Leitweg-ID oder
 * Rechnungsformat nicht mitschickte, setzte sie beim Speichern still zurück. Beim Anlegen gehen sie seit 816 ebenso mit.
 */
export function masterFrom(d: PartnerDraft): KundeStamm {
  return {
    companyName: d.companyName, street: orNull(d.street), houseNumber: orNull(d.houseNumber),
    postalCode: orNull(d.postalCode), city: orNull(d.city), phone: orNull(d.phone), email: orNull(d.email),
    vatId: orNull(d.vatId), industry: orNull(d.industry), website: orNull(d.website), country: d.country,
    leitwegId: orNull(d.leitwegId), invoiceFormat: d.invoiceFormat,
  };
}

/** Die Felder, in denen sich zwei Entwürfe unterscheiden (Folge von `PARTNER_FIELDS`). */
export const changedFields = (a: PartnerDraft, b: PartnerDraft): Field[] =>
  PARTNER_FIELDS.filter((f) => a[f.name] !== b[f.name]).map((f) => f.name);

/** Ab so vielen Zeichen sucht die Anlege-Maske Dubletten, und der Firmenname geht als Hinweis an die Anreicherung. */
export const MIN_NAME_LENGTH = 3;
/** Entprellung der Anreicherung — dahinter hängen fremde Quellen (VIES, Impressum, Webseite). */
const ENRICHMENT_DELAY_MS = 500;

/** Das Attribut `breit` an einem Kind ohne eigene Prop dafür: Das zweispaltige Formular lässt sein Paar beide Spalten
 *  spannen (me-design 134). Auch für die Slots der Aufrufer (Dublettentreffer). */
export const BREIT: Readonly<Record<string, string>> = { breit: "" };

/**
 * Der Verlassen-Wächter beider Modi: registriert, solange `geaendert`, und dasselbe für das ECHTE Verlassen
 * (`beforeunload`). Der Rückgabewert meldet ihn sofort ab — für Erfolgswege, die selbst navigieren (die Effekt-Aufräumung
 * liefe erst nach der Navigation und fragte dazwischen).
 */
export function usePartnerLeaveGuard(geaendert: boolean,
  registriere: (frage: VerlassenWaechter) => () => void): { current: (() => void) | null } {
  const abmelden = useRef<(() => void) | null>(null);
  const { bestaetigen } = useBestaetigung();
  useEffect(() => {
    if (!geaendert) return;
    const ab = registriere(() => bestaetigen({
      titel: T.leaveTitle, text: T.leaveText, bestaetigenText: T.leaveConfirm, gefaehrlich: true,
    }));
    abmelden.current = ab;
    return () => { abmelden.current = null; ab(); };
  }, [geaendert, registriere]);
  useBeforeUnload(geaendert);
  return abmelden;
}

/**
 * Die Anreicherung (817): Vorschläge aus VIES, Impressum und Webseite, entprellt, eine kranke Quelle still — sie hält das
 * Speichern NIE auf. Gefragt wird nur mit den Auslösern, die vom Ausgangsstand `base` abweichen: beim Anlegen (gegen den
 * leeren Entwurf) mit allem Eingetragenen, beim Bearbeiten nur mit der geänderten USt-IdNr. bzw. Webseite (Review K1) —
 * das Öffnen allein sucht nie, und eine unveränderte Webseite liefert keine Impressum-Vorschläge zu Feldern, die niemand
 * angefasst hat. Der Firmenname geht ab drei Zeichen als Hinweis mit, löst aber selbst nichts aus.
 *
 * Die Antwort gilt nur für ihren Schlüssel (gefragte USt-IdNr. | Webseite; Review W1, Muster `fetchedFor` der
 * Logo-Kacheln): Ändert sich ein Auslöser, verschwinden VIES-Befund und Vorschläge sofort — auch wenn der neue Abruf
 * scheitert, steht kein Befund zu einer Nummer da, die nie geprüft wurde. Die Logo-Kandidaten der Antwort bleiben außen
 * vor: Logos holt in beiden Modi der Knopf hinter der Webseite.
 */
function useSuggestions(draft: PartnerDraft, base: PartnerDraft, lookup: ApiClient["lookupEnrichment"]): FieldSuggestion[] {
  const [answer, setAnswer] = useState<{ key: string; suggestions: FieldSuggestion[] } | null>(null);
  const vatId = draft.vatId.trim() === base.vatId.trim() ? "" : draft.vatId.trim();
  // Ohne Schema mit https://, Text ohne Domain fragt nicht an — dieselbe Regel wie der Logo-Knopf. Das Feld bleibt.
  const website = draft.website.trim() === base.website.trim() ? null : websiteForLookup(draft.website);
  const key = vatId === "" && website === null ? null : `${vatId}|${website ?? ""}`;
  const name = draft.companyName.trim();
  useEffect(() => {
    if (key === null) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      lookup({
        ...(vatId === "" ? {} : { vatId }), ...(website === null ? {} : { website }),
        ...(name.length >= MIN_NAME_LENGTH ? { companyName: name } : {}),
      }).then((a) => { if (!cancelled) setAnswer({ key, suggestions: a.suggestions }); }).catch(() => {});
    }, ENRICHMENT_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [key, vatId, website, name, lookup]);
  return answer !== null && answer.key === key ? answer.suggestions : [];
}

export type PartnerDialogClient = Pick<ApiClient, "listLaender" | "listPlzOrte" | "lookupEnrichment">;

export type GeschaeftspartnerDialogProps = {
  modus: "anlegen" | "bearbeiten";
  titel: string;
  client: PartnerDialogClient;
  draft: PartnerDraft;
  setDraft: Dispatch<SetStateAction<PartnerDraft>>;
  /** Der Ausgangsstand, an dem die Anreicherung „geändert“ misst (817): `EMPTY_DRAFT` bzw. der gespeicherte Stand. */
  base: PartnerDraft;
  fieldErrors: Readonly<Record<string, string | undefined>>;
  formularProps: ReturnType<typeof useFormularZustand>["formularProps"];
  /** Die Sperre der Maske (Schreibweg läuft, fremder Stand) — die Ortszeile fragt dann nichts nach. */
  gesperrt: boolean;
  /** Der Stand, an den der innere Zustand der Ortszeile gebunden ist (M-6a). */
  ortszeileKey: string;
  /** Die Logo-Wahl gehört zum Entwurf des Aufrufers und geht erst mit seinem Schreibweg an die Logo-Route. */
  logo: { chosen: string | null; onChosen(url: string | null): void; existing: string | null };
  /** Slot „fremder Stand“ des Formulars (nur Bearbeiten). */
  hinweis?: ReactNode;
  /** Unter dem Firmennamen, über die ganze Breite (nur Anlegen: Dublettentreffer). */
  nachFirmenname?: ReactNode;
  /** Am Ende des Formulars (nur Anlegen: „Beziehung bei …“). */
  beziehung?: ReactNode;
  meldungen?: ReactNode;
  fuss: ReactNode;
  onAbbrechen(): void | Promise<void>;
  abbrechenGesperrt: boolean;
};

/**
 * Rahmen beider Modi (815): rechts angeschlagenes Panel (`Arbeitsdialog panel zweispaltig`, Hausregel 198) mit EINEM
 * `Formular zweispaltig`, Beschriftung über dem Feld (die Ortszeile bringt drei eigene Beschriftungen in einer Zeile mit,
 * daneben stünde eine Beschriftung links schief). Maße nur aus me-design: `breit` an Firmenname, Ortszeile, Logo-Kacheln
 * und Slots, `kurz` an der Hausnummer; unter der Formular-Grenze und am Telefon einspaltig (me-design regelt das).
 */
export function GeschaeftspartnerDialog(p: GeschaeftspartnerDialogProps): ReactElement {
  const { draft, setDraft, fieldErrors: fehler } = p;
  const laender = useLaender(p.client.listLaender);
  const suggestions = useSuggestions(draft, p.base, p.client.lookupEnrichment);
  const [logoCandidates, setLogoCandidates] = useState<readonly LogoCandidate[]>([]);
  const setze = (name: Field) => (wert: string): void => setDraft((d) => ({ ...d, [name]: wert }));

  /** Die Vorschlagsleiste unter einem Feld: Wert, Quelle und ein Knopf, der SICHTBAR nur dieses Feld setzt. Trägt das
   *  Feld den Wert schon, steht „übernommen“ — nichts wird je still ersetzt. */
  function vorschlag(name: Field): ReactElement | null {
    const v = suggestions.find((s) => s.field === name);
    if (v === undefined) return null;
    return (
      <div className="gpd__vorschlag" role="group" aria-label={T.suggestionFor(T.field[name])}>
        <span className="gpd__vorschlag-wert">{v.value}</span>
        <span className="gpd__vorschlag-quelle">{SOURCE_LABEL[v.source]}</span>
        {draft[name] === v.value
          ? <span className="gpd__vorschlag-uebernommen">{T.suggestionTaken}</span>
          : <KiKnopf className="gpd__vorschlag-knopf" onClick={() => setze(name)(v.value)}>{T.takeSuggestion}</KiKnopf>}
      </div>
    );
  }

  /** Ein Stammdatenfeld samt Vorschlag als EINE Zelle des Rasters — der Vorschlag erscheint ohne Layout-Sprung darunter. */
  function zelle(name: Field, o: { typ?: "tel" | "email"; kurz?: boolean; breit?: boolean; hilfe?: string;
    unten?: ReactNode } = {}): ReactElement {
    return (
      <div className="gpd__zelle" {...(o.breit === true ? BREIT : {})}>
        <Feld id={partnerFieldId(name)} label={T.field[name]} wert={draft[name]} bearbeitbar onChange={setze(name)}
          {...(o.typ === undefined ? {} : { typ: o.typ })} kurz={o.kurz} hilfe={o.hilfe} fehler={fehler[name]} />
        {o.unten}
        {vorschlag(name)}
      </div>
    );
  }

  // Der VIES-Befund — ein Hinweis an der USt-IdNr., KEIN übernehmbarer Wert; der Text ist der zugängliche Name.
  const vies = suggestions.find((s) => s.field === "vatIdValid");
  const viesGueltig = vies?.value === "true";
  const viesText = viesGueltig ? T.viesValid : T.viesInvalid;
  const viesHinweis = vies === undefined ? null : (
    <span className={`gpd__vies gpd__vies--${viesGueltig ? "gueltig" : "ungueltig"}`} role="status" aria-label={viesText}>
      <span aria-hidden="true">{viesGueltig ? "✓" : "✗"}</span> {viesText}
    </span>
  );

  return (
    <Arbeitsdialog titel={p.titel} panel="partner" zweispaltig onAbbrechen={p.onAbbrechen}
      abbrechenGesperrt={p.abbrechenGesperrt} {...(p.meldungen === undefined ? {} : { meldungen: p.meldungen })}
      fuss={p.fuss}>
      <Formular zweispaltig beschriftung="oben" className="gpd" {...p.formularProps} hinweis={p.hinweis}>
        {zelle("companyName", { breit: true })}
        {p.nachFirmenname}
        {zelle("street")}
        {zelle("houseNumber", { kurz: true })}
        {/* Land, PLZ und Ort in EINER Zeile (Spec „Adresszeile“, E400); ihre Vorschläge darunter. */}
        <div className="gpd__zelle" {...BREIT}>
          <Ortszeile key={p.ortszeileKey} praefix={ID_PREFIX} laender={laender} plzOrte={p.client.listPlzOrte}
            gesperrt={p.gesperrt} fehler={fehler}
            werte={{ country: draft.country, postalCode: draft.postalCode, city: draft.city }}
            onAenderung={(teil) => setDraft((d) => ({ ...d, ...teil }))} />
          {vorschlag("postalCode")}
          {vorschlag("city")}
        </div>
        {zelle("phone", { typ: "tel" })}
        {zelle("email", { typ: "email" })}
        {/* Die Webseite mit dem Logo-Knopf direkt dahinter (817, Entscheid 673). Die Beschriftung steht als <label for>
            über der Zeile, damit Knopf und Feld oben bündig stehen, auch wenn unter dem Feld der Hilfetext steht. */}
        <label htmlFor={partnerFieldId("website")}>{T.field.website}</label>
        <WebsiteLogoLookup website={draft.website} lookup={p.client.lookupEnrichment}
          candidates={logoCandidates} onCandidates={setLogoCandidates}
          chosen={p.logo.chosen} onChosen={p.logo.onChosen} below={vorschlag("website")}>
          <Eingabe id={partnerFieldId("website")} name={partnerFieldId("website")} wert={draft.website}
            hilfe={T.websiteHelp}
            {...(fehler.website === undefined ? {} : { fehler: fehler.website })}
            onGeaendert={(d) => setze("website")(d.wert)} />
        </WebsiteLogoLookup>
        {zelle("vatId", { hilfe: T.vatIdHelp, unten: viesHinweis })}
        {zelle("industry")}
        <Auswahl id={partnerFieldId("invoiceFormat")} name={partnerFieldId("invoiceFormat")}
          beschriftung={T.field.invoiceFormat}
          wert={draft.invoiceFormat} {...(fehler.invoiceFormat === undefined ? {} : { fehler: fehler.invoiceFormat })}
          onGeaendert={(d) => setDraft((e) => ({ ...e, invoiceFormat: d.wert as InvoiceFormat }))}>
          {INVOICE_FORMATS.map((f) => <option key={f} value={f}>{T.invoiceFormats[f]}</option>)}
        </Auswahl>
        <Feld id={partnerFieldId("leitwegId")} label={T.field.leitwegId} wert={draft.leitwegId} bearbeitbar
          onChange={setze("leitwegId")} fehler={fehler.leitwegId} />
        {/* Bei der Wahl, nicht erst als 422: Wer XRechnung wählt, soll die Bedingung sehen (816, beide Modi). */}
        {draft.invoiceFormat === "XRECHNUNG_30" && draft.leitwegId.trim() === ""
          && <Hinweis art="info">{T.xrechnungNeedsLeitweg}</Hinweis>}
        {showsLogoChoice(logoCandidates, p.logo.chosen) && (
          <div className="gpd__zelle" {...BREIT}>
            <LogoChoice name={partnerFieldId("logo")} candidates={logoCandidates} chosen={p.logo.chosen}
              onChosen={p.logo.onChosen} existingLogo={p.logo.existing}
              help={p.modus === "anlegen" ? LOGO_TEXT.helpCreate
                : p.logo.existing === null ? LOGO_TEXT.helpEdit : LOGO_TEXT.helpReplace} />
          </div>
        )}
        {p.beziehung}
      </Formular>
    </Arbeitsdialog>
  );
}
